import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Horizontal-scroll wrapper that adds desktop chevron buttons + edge-fade
 * visual cues so users know more content is available off-screen.
 *
 * Mobile: relies on native touch-scroll (no chevrons, no fade).
 * Desktop (md+): shows left/right chevron buttons when overflow exists,
 *   plus a soft right/left gradient at the edges that fades content into
 *   the background, signalling more is scrollable in that direction.
 *
 * Drop-in replacement for an existing `<div className="overflow-x-auto …">`
 * — just wrap the children with <HScroller>…</HScroller>.
 */
const HScroller = ({ children, ariaLabel = 'Scrollable row', className = '', testId, autoSlide = false, autoSlideSpeed = 18 }) => {
  const ref = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  // iter107 — user chevron taps pause the auto-slide loop briefly so the two
  // controls don't fight each other.
  const chevronPauseUntil = useRef(0);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    update();
    const el = ref.current;
    if (!el) return;
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [update]);

  // Re-measure after children mount/change (e.g., categories load async).
  // Uses ResizeObserver on the scroll container's inner content.
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, [update]);

  // Auto-slide: gently ping-pong the strip so users on every device see
  // that off-screen content exists. Pauses while the user is actively
  // interacting (touch, mouse drag, wheel, chevron tap, hover) and resumes
  // as soon as interaction ends. Runs on desktop / tablet / mobile alike.
  useEffect(() => {
    if (!autoSlide) return;
    const el = ref.current;
    if (!el) return;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;
    let direction = 1; // 1 = right, -1 = left
    let userInteracting = false;
    let pauseUntil = 0;
    let edgeBoostUntil = 0;
    const startInteract = () => { userInteracting = true; };
    const endInteract = () => { userInteracting = false; pauseUntil = Date.now() + 900; };
    // Touch (mobile / tablet)
    el.addEventListener('touchstart', startInteract, { passive: true });
    el.addEventListener('touchend',   endInteract,   { passive: true });
    el.addEventListener('touchcancel', endInteract,  { passive: true });
    // Mouse drag / hover (desktop)
    el.addEventListener('mouseenter', startInteract);
    el.addEventListener('mouseleave', endInteract);
    el.addEventListener('mousedown',  startInteract);
    el.addEventListener('mouseup',    endInteract);
    // Wheel — user actively scrolling with the trackpad / wheel
    const onWheel = () => { pauseUntil = Date.now() + 1500; };
    el.addEventListener('wheel', onWheel, { passive: true });
    let rafId;
    let lastTs = 0;
    const PX_PER_SEC = autoSlideSpeed;
    const BOOST_MULTIPLIER = 2.4;
    const BOOST_DURATION_MS = 700;
    const step = (ts) => {
      if (!lastTs) lastTs = ts;
      const dt = (ts - lastTs) / 1000;
      lastTs = ts;
      if (!userInteracting && Date.now() > pauseUntil && Date.now() > chevronPauseUntil.current) {
        const maxScroll = el.scrollWidth - el.clientWidth;
        if (maxScroll <= 0) {
          rafId = window.requestAnimationFrame(step);
          return;
        }
        const speed = Date.now() < edgeBoostUntil
          ? PX_PER_SEC * BOOST_MULTIPLIER
          : PX_PER_SEC;
        el.scrollLeft += direction * speed * dt;
        if (el.scrollLeft >= maxScroll - 1) {
          el.scrollLeft = maxScroll;
          direction = -1;
          pauseUntil = Date.now() + 900;
          edgeBoostUntil = Date.now() + 900 + BOOST_DURATION_MS;
        } else if (el.scrollLeft <= 1) {
          el.scrollLeft = 0;
          direction = 1;
          pauseUntil = Date.now() + 900;
          edgeBoostUntil = Date.now() + 900 + BOOST_DURATION_MS;
        }
      }
      rafId = window.requestAnimationFrame(step);
    };
    rafId = window.requestAnimationFrame(step);
    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      el.removeEventListener('touchstart', startInteract);
      el.removeEventListener('touchend',   endInteract);
      el.removeEventListener('touchcancel', endInteract);
      el.removeEventListener('mouseenter', startInteract);
      el.removeEventListener('mouseleave', endInteract);
      el.removeEventListener('mousedown',  startInteract);
      el.removeEventListener('mouseup',    endInteract);
      el.removeEventListener('wheel',      onWheel);
    };
  }, [autoSlide, autoSlideSpeed]);

  const scrollBy = (direction) => {
    const el = ref.current;
    if (!el) return;
    // Viewport-aware page jump: 70% of the visible strip width, floored at
    // 200px so a click always moves noticeably even on narrow phones.
    const delta = Math.max(200, Math.round(el.clientWidth * 0.7)) * direction;
    el.scrollBy({ left: delta, behavior: 'smooth' });
    // Pause the auto-slide loop for 3.5s so the user's intent isn't fought.
    chevronPauseUntil.current = Date.now() + 3500;
  };

  return (
    <div className={`relative ${className}`} data-testid={testId}>
      {/* Left chevron — visible on DESKTOP (lg+) only; mobile & tablet rely
          entirely on touch-swipe + auto-slide, so the chevron would only
          obstruct the icons per user report. */}
      {canLeft && (
        <button
          aria-label={`Scroll ${ariaLabel} left`}
          data-testid={testId ? `${testId}-prev` : undefined}
          onClick={() => scrollBy(-1)}
          className="hidden lg:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white text-[#1800ad] border border-gray-200 shadow-md items-center justify-center hover:bg-[#f0edff] active:scale-95 transition-all"
          type="button"
        >
          <ChevronLeft size={20} />
        </button>
      )}
      {/* Right chevron — visible on DESKTOP (lg+) only, same reason as above. */}
      {canRight && (
        <button
          aria-label={`Scroll ${ariaLabel} right`}
          data-testid={testId ? `${testId}-next` : undefined}
          onClick={() => scrollBy(1)}
          className="hidden lg:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white text-[#1800ad] border border-gray-200 shadow-md items-center justify-center hover:bg-[#f0edff] active:scale-95 transition-all"
          type="button"
        >
          <ChevronRight size={20} />
        </button>
      )}

      {/* Edge fade gradients — universal cue that more content exists */}
      {canLeft && (
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-white to-transparent z-[5]" />
      )}
      {canRight && (
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-white to-transparent z-[5]" />
      )}

      <div ref={ref} className="overflow-x-auto scrollbar-hide">
        {children}
      </div>
    </div>
  );
};

export default HScroller;
